(() => {
  if (customElements.get('mv-video-story')) return;
  class VideoStory extends HTMLElement {
    connectedCallback() {
      if (this.startupFrame || this.abort) return;
      this.startupFrame = requestAnimationFrame(() => {
        this.startupFrame = 0;
        if (this.isConnected) this.initialize();
      });
    }
    initialize() {
      if (this.abort) return;
      this.abort = new AbortController();
      this.mobile = matchMedia('(max-width: 749px)');
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.stage = this.querySelector('.mv-vs__stage');

      this.titleLayer = this.querySelector('.mv-vs__title');
      this.videoSlot = this.querySelector('.mv-vs__video-slot');
      this.button = this.querySelector('.mv-vs__toggle');
      this.status = this.querySelector('.mv-vs__status');
      this.near = false;
      this.visible = false;
      this.manualPaused = false;
      this.userStarted = false;
      this.pendingPlay = false;
      this.generation = 0;
      this.schedule = () => {
        if (!this.frame) this.frame = requestAnimationFrame(() => {
          this.frame = 0;
          this.updateAnimation();
        });
      };
      this.onEnvironment = () => {
        this.configureAnimation();
        this.resetVideo();
        if (this.near && !this.reduced.matches) this.loadVideo();
        this.schedule();
      };
      this.mobile.addEventListener('change', this.onEnvironment);
      this.reduced.addEventListener('change', this.onEnvironment);
      const options = { passive: true, signal: this.abort.signal };

      window.addEventListener('scroll', this.schedule, options);
      window.addEventListener('resize', this.schedule, options);
      document.addEventListener('visibilitychange', () => this.syncPlayback(), { signal: this.abort.signal });
      this.button?.addEventListener('click', () => this.togglePlayback(), { signal: this.abort.signal });
      this.configureAnimation();
      if ('IntersectionObserver' in window) {
        this.preloadObserver = new IntersectionObserver(entries => {
          this.near = entries[0].isIntersecting;
          if (this.near && !this.reduced.matches) this.loadVideo();
          this.schedule();
        }, { rootMargin: '500px 0px' });
        this.playObserver = new IntersectionObserver(entries => {
          const entry = entries[0];
          this.visible = entry.isIntersecting && entry.intersectionRatio >= 0.15;
          this.syncPlayback();
        }, { threshold: [0, 0.15, 0.5] });
        this.preloadObserver.observe(this);
        this.playObserver.observe(this.stage);
      } else {
        this.near = true;
        this.visible = true;
        if (!this.reduced.matches) this.loadVideo();
      }
      if (this.button && this.hasVideo()) this.button.hidden = false;
      this.schedule();
    }
    disconnectedCallback() {
      cancelAnimationFrame(this.startupFrame);
      this.startupFrame = 0;
      this.abort?.abort();
      this.abort = null;
      this.preloadObserver?.disconnect();
      this.playObserver?.disconnect();
      this.mobile?.removeEventListener('change', this.onEnvironment);
      this.reduced?.removeEventListener('change', this.onEnvironment);
      cancelAnimationFrame(this.frame);
      this.frame = 0;
      this.resetVideo();
    }
    hasVideo() {
      return Boolean(this.querySelector('template[data-video]'));
    }
    configureAnimation() {
      this.classList.toggle('has-title-animation', this.dataset.titleAnimation === 'true' && Boolean(this.titleLayer) && !this.reduced.matches);
    }
    updateAnimation() {
      if (!this.titleLayer) return;
      if (!this.classList.contains('has-title-animation')) {
        this.style.removeProperty('--vs-title-y');
        this.style.removeProperty('--vs-title-opacity');
        return;
      }
      if (!this.near) return;
      const style = getComputedStyle(this);
      const header = parseFloat(getComputedStyle(this.stage).top) || 0;
      const padding = parseFloat(style.paddingTop) || 0;
      const distance = parseFloat(style.getPropertyValue('--vs-active-distance')) || 400;
      const travel = Math.max(0, header - this.getBoundingClientRect().top - padding);
      const progress = Math.min(1, travel / distance);
      const height = this.stage.getBoundingClientRect().height;
      const titleHeight = this.titleLayer.querySelector('.mv-vs__title-inner').getBoundingClientRect().height;
      const offset = -(height / 2 + titleHeight / 2 + 32) * progress;
      const opacity = Math.max(0, Math.min(1, (1 - progress) / 0.35));
      this.style.setProperty('--vs-title-y', offset.toFixed(2) + 'px');
      this.style.setProperty('--vs-title-opacity', opacity.toFixed(3));
    }
    resetVideo() {
      clearTimeout(this.playRetryTimer);
      this.playRetryTimer = 0;
      this.autoplayRetries = 0;
      this.generation++;
      this.pendingPlay = false;
      if (this.video) {
        this.video.pause();
        this.video.removeAttribute('src');
        this.video.querySelectorAll('source').forEach(source => source.removeAttribute('src'));
        this.video.load();
      }
      this.video = null;
      this.videoSlot?.replaceChildren();
      this.classList.remove('is-playing', 'has-frame');
      this.failed = false;
      if (this.status) this.status.hidden = true;
      this.updateButton();
    }
    loadVideo() {
      if (this.video || !this.abort) return;
      const mode = this.mobile.matches ? 'mobile' : 'desktop';
      const template = this.querySelector('template[data-video="' + mode + '"]');
      if (!template) return;
      this.videoSlot.append(template.content.cloneNode(true));
      this.video = this.videoSlot.querySelector('video');
      const video = this.video;
      if (!video) return;
      video.muted = true;
      video.defaultMuted = true;
      video.setAttribute('muted', '');
      video.setAttribute('playsinline', '');
      video.playsInline = true;
      video.removeAttribute('autoplay');
      if (this.dataset.decorative === 'true') {
        video.setAttribute('aria-hidden', 'true');
        video.setAttribute('tabindex', '-1');
      } else {
        video.setAttribute('aria-label', this.dataset.description || this.dataset.playLabel);
      }
      const generation = this.generation;
      const on = (type, callback) => video.addEventListener(type, () => {
        if (generation === this.generation) callback();
      }, { signal: this.abort.signal });
      on('canplay', () => this.syncPlayback());
      on('loadeddata', () => this.syncPlayback());
      on('playing', () => {
        clearTimeout(this.playRetryTimer);
        this.autoplayRetries = 0;
        this.failed = false;
        if (this.status) this.status.hidden = true;
        this.classList.add('is-playing', 'has-frame');
        this.updateButton();
        if (!this.visible || document.hidden) video.pause();
      });
      on('pause', () => {
        this.classList.remove('is-playing');
        this.updateButton();
      });
      on('ended', () => {
        this.manualPaused = true;
        this.classList.remove('is-playing');
        this.updateButton();
      });
      on('error', () => {
        this.failed = true;
        this.classList.remove('is-playing', 'has-frame');
        if (this.status) this.status.hidden = false;
        if (this.button) this.button.hidden = false;
        this.updateButton();
      });
      video.load();
      this.syncPlayback();
    }
    syncPlayback() {
      const video = this.video;
      if (!video) return;
      const automatic = this.dataset.autoplay === 'true' && !this.reduced.matches;
      const shouldPlay = this.visible && !document.hidden && !this.manualPaused && (automatic || this.userStarted);
      video.autoplay = shouldPlay;
      if (!shouldPlay) {
        clearTimeout(this.playRetryTimer);
        video.pause();
        return;
      }
      if (this.failed || this.pendingPlay || !video.paused) return;
      this.pendingPlay = true;
      const generation = this.generation;
      Promise.resolve(video.play()).catch(() => {
        if (generation !== this.generation) return;
        this.autoplayRetries = (this.autoplayRetries || 0) + 1;
        this.classList.remove('is-playing');
        if (this.button) this.button.hidden = false;
      }).finally(() => {
        if (generation !== this.generation) return;
        this.pendingPlay = false;
        this.updateButton();
        if (!this.visible || document.hidden || this.manualPaused) {
          video.pause();
        } else if (video.paused && this.autoplayRetries > 0 && this.autoplayRetries <= 3) {
          clearTimeout(this.playRetryTimer);
          this.playRetryTimer = setTimeout(() => {
            if (generation === this.generation && this.isConnected) this.syncPlayback();
          }, this.autoplayRetries * 500);
        }
      });
    }
    togglePlayback() {
      if (this.video && !this.video.paused) {
        this.manualPaused = true;
        this.video.pause();
        return;
      }
      if (this.failed) this.resetVideo();
      this.manualPaused = false;
      this.userStarted = true;
      this.loadVideo();
      this.syncPlayback();
    }
    updateButton() {
      if (!this.button) return;
      const playing = Boolean(this.video && !this.video.paused);
      this.button.setAttribute('aria-label', playing ? this.dataset.pauseLabel : this.dataset.playLabel);
      this.button.hidden = playing && this.dataset.controls !== 'true';
      if (!playing && !this.hasVideo()) this.button.hidden = true;
    }
  }
  customElements.define('mv-video-story', VideoStory);
})();